import { useEffect, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faPause,
  faStop,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./styles.module.css";

const TimeTracker = ({
  selectedTab,
  onStateChange,
  elapsedTime,
  setElapsedTime,
  isTracking,
  setIsTracking,
  id,
  resetTimer,
  setResetTimer,
  // May 2026 redesign: when true, render the compact header pill
  // (pause-first, no stop button, cream-cyan-pale fill, dark navy text).
  // Default false preserves the legacy visuals for any other consumer.
  headerStyle = false,
}) => {
  const instanceId = useRef(id || Math.random().toString(36).substr(2, 9));

  const startTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const pausedTimeRef = useRef(0);

  // Ref to track isTracking for synchronous reads
  const isTrackingRef = useRef(true);
  isTrackingRef.current = isTracking;

  // Flag to prevent auto-restart after manual stop/reset
  const manualStopRef = useRef(false);

  // Increment elapsed time once per second. The previous version gated CCM
  // increments behind a `careplan_created` sessionStorage flag; the
  // June 2026 JIRA "Time tracking updated requirements" removed that gate
  // so the CCM timer starts as soon as the user enters the CCM section.
  const startTracking = () => {
    // Clear manual stop flag when user explicitly clicks Play
    manualStopRef.current = false;

    // Clear any existing interval first to prevent duplicates
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsTracking(true);
    startTimeRef.current = new Date().toISOString();
    intervalRef.current = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);
  };

  // Function to pause tracking
  const pauseTracking = () => {
    if (isTrackingRef.current) {
      manualStopRef.current = true;
      setIsTracking(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      pausedTimeRef.current = new Date().toISOString();
    }
  };

  const handleStop = useCallback(() => {
    manualStopRef.current = true;

    setIsTracking(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setElapsedTime(0);
    startTimeRef.current = null;
    pausedTimeRef.current = null;
  }, [setElapsedTime, setIsTracking]);

  // Auto-start tracking on initial mount only. Splitting the original
  // single-effect (mount auto-start + reset-watch) prevents the timer
  // from auto-resuming the moment the parent flips resetTimer back to
  // false — after reset, it now stays at 00:00:00 until the user hits
  // the play/pause button.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { startTracking(); }, []);

  // Watch for the reset signal from the parent. When the parent sets
  // resetTimer=true, stop the timer (clears interval + zeros elapsed)
  // and acknowledge by clearing the flag. Do NOT restart tracking.
  useEffect(() => {
    if (resetTimer) {
      handleStop();
      setResetTimer(false);
    }
  }, [resetTimer, handleStop, setResetTimer]);

  useEffect(() => {
    // Don't start if user manually stopped (pause, discard, or save)
    if (isTracking && !manualStopRef.current) {
      // Clear any interval that already exists before creating a new one.
      // isTracking initializes true, so this effect's FIRST run lands right
      // after the mount auto-start created an interval; without this clear
      // that one would be orphaned and the timer would count 2 seconds per
      // second (and keep counting through pause/stop).
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      startTimeRef.current = new Date().toISOString();
      intervalRef.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [selectedTab, isTracking, setElapsedTime]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    onStateChange({
      elapsedTime,
    });
  }, [elapsedTime]);

  // Format elapsed time. The redesigned header pill drops the hours
  // segment per the June 2026 QA pass: clinical sessions are minute-
  // scale, so MM:SS reads cleaner (and 90 minutes shows as `90:00`,
  // not `1:30:00`). DailiesOORTracker mirrors this format; legacy
  // non-headerStyle consumers still get HH : MM : SS.
  const formatTime = (seconds) => {
    const totalMinutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const s = remainingSeconds.toString().padStart(2, "0");
    if (headerStyle) {
      const mm = totalMinutes.toString().padStart(2, "0");
      return `${mm}:${s}`;
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = totalMinutes % 60;
    const h = hours.toString().padStart(2, "0");
    const m = minutes.toString().padStart(2, "0");
    return `${h} : ${m} : ${s}`;
  };

  if (headerStyle) {
    return (
      <div
        className={`${styles.monitoring_time_wrapper} ${styles.headerStyle}`}
        data-instance-id={instanceId.current}
      >
        <button
          type="button"
          className={styles.headerCircleBtn}
          onClick={() => (isTracking ? pauseTracking() : startTracking())}
          aria-label={isTracking ? "Pause monitoring" : "Resume monitoring"}
        >
          <FontAwesomeIcon
            icon={isTracking ? faPause : faPlay}
            className={styles.headerCircleIcon}
          />
        </button>
        <div className={styles.headerPill}>
          <span className={styles.headerPillTime}>{formatTime(elapsedTime)}</span>
          <span className={styles.headerPillSuffix}>{selectedTab.toUpperCase()}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.monitoring_time_wrapper}
      data-instance-id={instanceId.current}
    >
      {" "}
      <div className={styles.monitoring_time} data-type={selectedTab}>
        {formatTime(elapsedTime)}
        <p>{selectedTab.toUpperCase()}</p>
      </div>
      <div
        data-type={selectedTab}
        className={styles.controls_btn_container}
        onClick={() => (isTracking ? pauseTracking() : startTracking())}
      >
        {isTracking ? (
          <FontAwesomeIcon className={styles.play_btn} icon={faPause} />
        ) : (
          <FontAwesomeIcon className={styles.play_btn} icon={faPlay} />
        )}
      </div>
      <div
        data-type={selectedTab}
        className={styles.controls_btn_container}
        onClick={() => handleStop()}
      >
        <FontAwesomeIcon className={styles.play_btn} icon={faStop} />
      </div>
    </div>
  );
};

export default TimeTracker;